import type { AppRole } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface TeamDto {
  id: string;
  organisation_id: string;
  name: string;
  slug: string;
  description: string | null;
  discipline: string | null;
  colour: string | null;
  member_count: number;
  created_at: string;
  updated_at: string | null;
}

export type TeamRole = 'member' | 'leader';

export interface TeamMemberDto {
  team_id: string;
  user_id: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
  app_role: AppRole;
  // Per-team role. 'leader' grants reporting access in the dashboard scoped to
  // this team (in addition to whatever the worker's app_role allows).
  role: TeamRole;
  joined_at: string;
}

export interface CreateTeamInput {
  name: string;
  slug?: string;
  description?: string | null;
  discipline?: string | null;
  colour?: string | null;
}

export interface UpdateTeamInput extends CreateTeamInput {}

export function useTeamsList() {
  const api = useApi();
  return useQuery({
    queryKey: ['teams', 'list'],
    queryFn: () => api<{ teams: TeamDto[] }>('/teams'),
    staleTime: 30_000,
  });
}

export function useTeam(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['teams', id],
    queryFn: () => api<{ team: TeamDto; members: TeamMemberDto[] }>(`/teams/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateTeam() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamInput) =>
      api<{ team: TeamDto }>('/teams', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useUpdateTeam(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTeamInput) =>
      api<{ team: TeamDto }>(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useDeleteTeam() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/teams/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useAddTeamMembers(teamId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      api<{ added: string[] }>(`/teams/${teamId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: userIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useRemoveTeamMember(teamId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

// Set a team member's per-team role (promote / demote). Admin only on the
// server. The team-detail and roster queries are invalidated so the leader
// badge appears immediately after a successful mutation.
export function useSetTeamMemberRole(teamId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) =>
      api<{ member: TeamMemberDto }>(`/teams/${teamId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}
